import React, { useState, useEffect } from 'react';
import { Heart, BookOpen, Calendar, Clock, MapPin } from 'lucide-react';
import ImageWithFallback from './ImageWithFallback';

const Hero = ({ openModal }) => {
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Images pour la galerie
  const communityImages = [
    {
      id: 1,
      src: "../assets/images/hero.JPG",
      title: "Famille unie",
      type: "family"
    },
    {
      id: 2,
      src: "../assets/images/hero_4.JPG",
      title: "Mariage béninois",
      type: "couple"
    },
    {
      id: 3,
      src: "../assets/images/h5.png",
      title: "Célébration",
      type: "family"
    },
    {
      id: 4,
      src: "../assets/images/hero_1.JPG",
      title: "Union sacrée",
      type: "couple"
    },
    {
      id: 5,
      src: "../assets/images/hero_3.JPG",
      title: "Messe familiale",
      type: "family"
    }
  ];

  // Fonction pour récupérer les événements depuis l'API
  const fetchEvents = async () => {
    try {
      const response = await fetch('http://localhost:8000/api/events/upcoming');
      if (response.ok) {
        const data = await response.json();
        const eventsWithIcons = data.data.slice(0, 3).map(event => ({
          ...event,
          icon: getEventIcon(event.category),
          targetAudience: getTargetAudience(event.category)
        }));
        setEvents(eventsWithIcons);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des événements:', error);
    } finally {
      setEventsLoading(false);
    }
  };

  // Fonction pour obtenir l'icône selon la catégorie
  const getEventIcon = (category) => {
    const icons = {
      messe: '⛪',
      priere: '🙏',
      formation: '📖',
      rencontre: '👥',
      retraite: '🕊️',
      celebration: '🎉'
    };
    return icons[category] || '📅';
  };

  // Fonction pour obtenir le public cible selon la catégorie
  const getTargetAudience = (category) => {
    const audiences = {
      messe: 'Tous les membres',
      priere: 'Tous les couples',
      formation: 'Couples et familles',
      rencontre: 'Communauté FSF',
      retraite: 'Couples mariés',
      celebration: 'Toute la famille'
    };
    return audiences[category] || 'Tous';
  };

  // Formatage de la date
  const formatEventDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };

  // Changement automatique des images toutes les 4 secondes
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % communityImages.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [communityImages.length]);

  // Récupération des événements au chargement du composant
  useEffect(() => {
    fetchEvents();
  }, []);

  return (
    <section id="accueil" className="relative overflow-hidden bg-gradient-to-br from-gray-50 to-white min-h-screen">
      {/* Hero principal - Layout 2 colonnes */}
      <div className="container mx-auto px-4 py-20">
        <div className="grid md:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
          
          {/* COLONNE GAUCHE : Contenu texte */}
          <div className="space-y-8">
            <div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight text-justify">
                Renforcez votre couple dans la foi
              </h1>
              
              <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-8 text-justify">
                Rejoignez la communauté <span className="font-bold text-amber-500">Fleurs de la Sainte Famille</span>, 
                un regroupement de couples mariés chrétiens catholiques au Bénin. Découvrez un nouvel espace 
                d'épanouissement spirituel et familial qui transformera votre vie de couple.
              </p>
            </div>

            {/* Boutons d'action */}
            <div className="flex flex-wrap gap-4">
              <button 
                onClick={openModal}
                className="bg-amber-500 text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-amber-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                Nous rejoindre
              </button>
              <a href="/about" 
                // onClick={() => {
                //   const aboutSection = document.getElementById('a-propos');
                //   if (aboutSection) {
                //     aboutSection.scrollIntoView({ behavior: 'smooth' });
                //   }
                // }}
                className="bg-transparent text-amber-500 px-8 py-4 rounded-xl font-bold text-lg border-2 border-amber-500 hover:bg-amber-50 transition-all duration-300 flex items-center space-x-2"
              >
                <span>Découvrir FSF</span>
                <span className="text-xl">→</span>
              </a>
            </div>

            {/* Statistiques */}
            <div className="flex flex-wrap gap-6 pt-4">
              <div>
                <p className="text-3xl md:text-4xl font-bold text-amber-500">+100</p>
                <p className="text-gray-600 font-medium">Couples inscrits</p>
              </div>
              <div>
                <p className="text-3xl md:text-4xl font-bold text-amber-500">+50</p>
                <p className="text-gray-600 font-medium">Événements/an</p>
              </div>
              <div>
                <p className="text-3xl md:text-4xl font-bold text-amber-500">+15</p>
                <p className="text-gray-600 font-medium">Années d'existence</p>
              </div>
            </div>
          </div>

          {/* COLONNE DROITE : Visuel avec cartes flottantes */}
          <div className="relative">
            {/* Image principale avec cercle */}
            <div className="relative">
              {/* Cercle décoratif en arrière-plan */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-br from-amber-200 to-amber-400 rounded-full opacity-30 blur-3xl animate-pulse-slow"></div>
              
              {/* Image principale avec animation et transition fluide */}
              <div className="relative z-10 w-full h-[500px] rounded-3xl overflow-hidden shadow-2xl transform hover:scale-105 transition-all duration-500">
                <ImageWithFallback
                  key={currentImageIndex}
                  src={communityImages[currentImageIndex]?.src}
                  fallbackType={communityImages[currentImageIndex]?.type}
                  alt={communityImages[currentImageIndex]?.title}
                  className="w-full h-full object-cover animate-slide-fade-in"
                />
                {/* Overlay léger */}
                <div className="absolute inset-0 bg-gradient-to-t from-amber-900/20 to-transparent"></div>
                
                {/* Indicateurs de pagination */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 z-10">
                  {communityImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-2 h-2 rounded-full transition-all duration-300 ${
                        index === currentImageIndex 
                          ? 'bg-white w-8' 
                          : 'bg-white/50 hover:bg-white/75'
                      }`}
                      aria-label={`Image ${index + 1}`}
                    />
                  ))}
                </div>
              </div>

              {/* Cartes flottantes DEVANT la photo - z-index élevé */}
              {/* Carte 1 - Top Right - Messes */}
              <div className="absolute -top-4 -right-4 z-20 bg-white rounded-2xl shadow-2xl p-4 animate-float border-2 border-amber-100 hover:shadow-3xl hover:scale-110 transition-all duration-300 cursor-pointer" style={{animationDelay: '0s'}}>
                <div className="flex items-center space-x-3">
                  <div className="bg-gradient-to-br from-amber-500 to-amber-600 p-3 rounded-xl shadow-lg">
                    <span className="text-2xl">⛪</span>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">50+</p>
                    <p className="text-sm text-gray-600 font-medium">Messes/an</p>
                  </div>
                </div>
              </div>

              {/* Carte 2 - Bottom Left - Couples */}
              <div className="absolute -bottom-6 -left-6 z-20 bg-white rounded-2xl shadow-2xl p-4 animate-float border-2 border-pink-100 hover:shadow-3xl hover:scale-110 transition-all duration-300 cursor-pointer" style={{animationDelay: '1s'}}>
                <div className="flex items-center space-x-3">
                  <div className="bg-gradient-to-br from-pink-500 to-pink-600 p-3 rounded-xl shadow-lg">
                    <span className="text-2xl">👨‍👩‍👧‍👦</span>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">100+</p>
                    <p className="text-sm text-gray-600 font-medium">Couples</p>
                  </div>
                </div>
              </div>

              {/* Carte 3 - Middle Right avec événement */}
              {events.length > 0 && (
                <div className="absolute top-1/2 -right-8 transform -translate-y-1/2 z-20 bg-gradient-to-br from-amber-500 to-amber-700 rounded-2xl shadow-2xl p-5 animate-float max-w-xs hover:shadow-3xl hover:scale-105 transition-all duration-300 cursor-pointer" style={{animationDelay: '0.5s'}}>
                  <div className="flex items-start space-x-3">
                    <div className="bg-white/20 p-2 rounded-lg backdrop-blur-sm">
                      <span className="text-2xl">{events[0].icon}</span>
                    </div>
                    <div className="text-white">
                      <p className="text-xs font-bold uppercase mb-1 text-amber-200">Prochain événement</p>
                      <p className="font-bold text-sm mb-1 line-clamp-2">{events[0].title}</p>
                      <p className="text-xs text-amber-200">{formatEventDate(events[0].date)}</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => {
                      const eventsSection = document.getElementById('evenements');
                      if (eventsSection) {
                        eventsSection.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                    className="mt-3 w-full bg-white text-amber-500 py-2 px-4 rounded-lg font-bold text-sm hover:bg-amber-50 transition-colors shadow-lg"
                  >
                    Participer
                  </button>
                </div>
              )}

              {/* Petite carte badge FSF - DEVANT */}
              <div className="absolute top-8 left-8 z-20 bg-white rounded-full shadow-xl px-5 py-3 flex items-center space-x-2 border-2 border-amber-100 animate-bounce-slow hover:scale-110 transition-all duration-300 cursor-pointer">
                <span className="text-2xl">✝️</span>
                <span className="font-bold text-gray-800">FSF Bénin</span>
              </div>

              {/* Carte supplémentaire - Bottom Right - Formations */}
              <div className="absolute -bottom-4 right-12 z-20 bg-white rounded-2xl shadow-2xl p-4 animate-float border-2 border-blue-100 hover:shadow-3xl hover:scale-110 transition-all duration-300 cursor-pointer" style={{animationDelay: '1.5s'}}>
                <div className="flex items-center space-x-3">
                  <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-3 rounded-xl shadow-lg">
                    <span className="text-2xl">📖</span>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">30+</p>
                    <p className="text-sm text-gray-600 font-medium">Formations</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section valeurs - 3 piliers */}
      <div className="bg-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Nos trois piliers fondamentaux
              </h2>
              <p className="text-xl text-gray-600">
                Une communauté bâtie sur des valeurs chrétiennes solides
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 border-2 border-blue-200 hover:border-blue-400 transition-all hover:shadow-xl">
                <div className="bg-blue-600 w-16 h-16 rounded-xl flex items-center justify-center mb-4">
                  <span className="text-3xl">📖</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Formation</h3>
                <p className="text-gray-700 leading-relaxed">
                  Enseignements bibliques approfondis, théologie du mariage et accompagnement spirituel continu
                </p>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-2xl p-8 border-2 border-amber-200 hover:border-amber-400 transition-all hover:shadow-xl">
                <div className="bg-amber-500 w-16 h-16 rounded-xl flex items-center justify-center mb-4">
                  <span className="text-3xl">⛪</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Prière</h3>
                <p className="text-gray-700 leading-relaxed">
                  Messes mensuelles, adorations eucharistiques, retraites spirituelles et moments de prière en couple
                </p>
              </div>

              <div className="bg-gradient-to-br from-pink-50 to-pink-100 rounded-2xl p-8 border-2 border-pink-200 hover:border-pink-400 transition-all hover:shadow-xl">
                <div className="bg-pink-600 w-16 h-16 rounded-xl flex items-center justify-center mb-4">
                  <span className="text-3xl">❤️</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Fraternité</h3>
                <p className="text-gray-700 leading-relaxed">
                  Partage authentique, entraide mutuelle, soutien dans les épreuves et communion familiale
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section des prochains événements */}
      {events.length > 1 && (
        <div className="bg-gradient-to-br from-amber-50 to-blue-50 py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 flex items-center justify-center space-x-3">
                  <Calendar className="w-10 h-10 text-amber-500" />
                  <span>Nos Prochains Événements</span>
                </h3>
                <p className="text-xl text-gray-600">Rejoignez-nous lors de nos activités spirituelles</p>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                {events.slice(0, 3).map((event) => (
                  <div key={event.id} className="bg-white hover:bg-gray-50 rounded-2xl p-6 transition-all duration-300 border-2 border-amber-200 hover:border-amber-400 shadow-md hover:shadow-xl">
                    <div className="flex items-center space-x-3 mb-4">
                      <span className="text-4xl">{event.icon}</span>
                      <h4 className="font-bold text-gray-900 text-xl">{event.title}</h4>
                    </div>
                    <div className="space-y-3 text-gray-700 mb-4">
                      <div className="flex items-center space-x-2">
                        <Calendar className="w-5 h-5 text-amber-500" />
                        <span className="text-sm font-medium">{formatEventDate(event.date)}</span>
                      </div>
                      {event.location && (
                        <div className="flex items-center space-x-2">
                          <MapPin className="w-5 h-5 text-amber-500" />
                          <span className="text-sm font-medium">{event.location}</span>
                        </div>
                      )}
                    </div>
                    <button 
                      onClick={() => {
                        const eventsSection = document.getElementById('evenements');
                        if (eventsSection) {
                          eventsSection.scrollIntoView({ behavior: 'smooth' });
                        }
                      }}
                      className="w-full bg-amber-500 text-white py-3 px-4 rounded-lg font-bold hover:bg-amber-600 transition-all duration-300 text-sm"
                    >
                      En savoir plus
                    </button>
                  </div>
                ))}
              </div>

              {events.length > 3 && (
                <div className="text-center mt-8">
                  <button 
                    onClick={() => {
                      const eventsSection = document.getElementById('evenements');
                      if (eventsSection) {
                        eventsSection.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                    className="bg-amber-500 text-white px-8 py-4 rounded-xl font-bold hover:bg-amber-600 transition-colors duration-300 shadow-lg"
                  >
                    Voir tous les événements
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Hero;
